import { z } from 'zod';
import { severitySchema } from './shared/severity';
import { PolicyKey } from './policies';
/**
 * AI Instruction Security Policy Catalog contracts (KIR-3515).
 *
 * The catalog is the baseline of security categories that AI instruction files
 * (CLAUDE.md, .cursorrules, …) are expected to cover. It is display + storage
 * only in this phase: nothing here drives scanner or endpoint-agent behavior.
 *
 * Frameworks and categories can be enabled/disabled and (partially) edited.
 * Built-in OWASP / MITRE ATLAS / NIST SSDF categories ship as static JSON;
 * per-org edits (severity, keywords, example) and enable/disable state layer on
 * top as overrides. Custom categories are fully persisted.
 */
/**
 * The four catalog frameworks. `owasp`, `atlas`, and `nist` ship built-in with
 * locked name + reference (their other fields are editable); `custom` holds the
 * out-of-the-box proprietary set plus any org-added categories (fully editable).
 */
export var CatalogFramework;
(function (CatalogFramework) {
    CatalogFramework["Owasp"] = "owasp";
    CatalogFramework["Atlas"] = "atlas";
    CatalogFramework["Nist"] = "nist";
    // KIR-3925: locked built-in framework of the Threat Modeling catalog (TM-01…09).
    CatalogFramework["ThreatModeling"] = "threat_modeling";
    CatalogFramework["Custom"] = "custom";
})(CatalogFramework || (CatalogFramework = {}));
export const catalogFrameworkSchema = z.enum([
    CatalogFramework.Owasp,
    CatalogFramework.Atlas,
    CatalogFramework.Nist,
    CatalogFramework.ThreatModeling,
    CatalogFramework.Custom,
]);
/**
 * Display metadata for the "AI Agent Instructions" group on the Policies page
 * (KIR-3816 renamed it from "AI Instruction Policies"). Single source of truth
 * shared by the backend policy registry and the frontend `POLICY_CATEGORIES_META`,
 * so the group heading + description can't drift.
 */
export const AI_INSTRUCTION_POLICY_GROUP = {
    name: 'AI Agent Instructions',
    description: 'Your rules for how AI coding agents should behave. Kirin audits instruction files (such as CLAUDE.md and .cursorrules) against this catalog and reports missing guidance. This is used for audit, not runtime enforcement.',
};
/**
 * The Secure AI Coding Enforcement catalog's frameworks in display order.
 * Other catalogs list theirs in {@link CATALOG_FRAMEWORKS_BY_ID}; per-catalog
 * display order is that list's order.
 */
export const CATALOG_FRAMEWORKS = [
    CatalogFramework.Owasp,
    CatalogFramework.Atlas,
    CatalogFramework.Nist,
    CatalogFramework.Custom,
];
export const CATALOG_FRAMEWORK_META = {
    [CatalogFramework.Owasp]: {
        framework: CatalogFramework.Owasp,
        label: 'OWASP Top 10',
        short: 'OWASP',
        version: 'OWASP Top 10:2021',
        description: 'The 10 official OWASP Top 10 (2021) categories, with instruction guidance for AI coding agents. For customers who follow OWASP.',
    },
    [CatalogFramework.Atlas]: {
        framework: CatalogFramework.Atlas,
        label: 'MITRE ATLAS',
        short: 'MITRE',
        version: 'MITRE ATLAS',
        description: 'Instruction guidance mapped to real MITRE ATLAS techniques, for customers who follow MITRE ATLAS. Review the mapping with your security team.',
    },
    [CatalogFramework.Nist]: {
        framework: CatalogFramework.Nist,
        label: 'NIST SSDF',
        short: 'NIST',
        version: 'NIST SP 800-218',
        description: 'Instruction guidance mapped to NIST SP 800-218 (SSDF) practices, for customers who must attest to NIST. SSDF-aligned interpretation — confirm the mapping with your security team.',
    },
    [CatalogFramework.ThreatModeling]: {
        framework: CatalogFramework.ThreatModeling,
        label: 'Threat Modeling Best Practices',
        short: 'Threat Modeling',
        version: '2024',
        description: 'Best practices that make AI coding agents model threats before they write code. This is the out of the box set and can be edited.',
    },
    [CatalogFramework.Custom]: {
        framework: CatalogFramework.Custom,
        label: 'Custom / Proprietary',
        short: 'Custom',
        version: 'v1.0',
        description: 'Categories your organization adds on its own. Five ship out of the box; use Add category to create more.',
    },
};
/**
 * KIR-3816: the two catalogs. `secure_coding` is the full framework catalog
 * (OWASP / MITRE ATLAS / NIST SSDF / Custom) shown on the Secure AI Coding
 * Enforcement page. `agent_instructions` is the separate single-framework
 * "AI Agent Instructions" catalog opened from the Policies page. They share the
 * same tables/handler/routes, isolated by the `catalog` column.
 */
export var CatalogId;
(function (CatalogId) {
    CatalogId["SecureCoding"] = "secure_coding";
    CatalogId["AgentInstructions"] = "agent_instructions";
    // KIR-3925: the Threat Modeling catalog, surfaced on its own AI AppSec page.
    CatalogId["ThreatModeling"] = "threat_modeling";
})(CatalogId || (CatalogId = {}));
export const catalogIdSchema = z.enum([
    CatalogId.SecureCoding,
    CatalogId.AgentInstructions,
    CatalogId.ThreatModeling,
]);
/**
 * Which frameworks each catalog exposes. `agent_instructions` reuses the
 * `custom` framework machinery but surfaces it as a single "AI Agent
 * Instructions" framework (see {@link catalogFrameworkLabel}).
 */
export const CATALOG_FRAMEWORKS_BY_ID = {
    [CatalogId.SecureCoding]: CATALOG_FRAMEWORKS,
    [CatalogId.AgentInstructions]: [CatalogFramework.Custom],
    [CatalogId.ThreatModeling]: [
        CatalogFramework.ThreatModeling,
        CatalogFramework.Custom,
    ],
};
/**
 * URL path segment for each catalog's routes, matching the backend mount
 * prefixes (`registerCatalogRoutes`). Single-sourced so the api-client base-path
 * builder and the backend registration can't drift.
 */
export const CATALOG_ROUTE_SEGMENT = {
    [CatalogId.SecureCoding]: 'ai-instruction-catalog',
    [CatalogId.AgentInstructions]: 'ai-agent-instructions',
    [CatalogId.ThreatModeling]: 'threat-modeling-catalog',
};
/**
 * The policy that controls each catalog's Off / Monitor mode. This is
 * the single expansion point for the per-project coverage dashboard (KIR-3517):
 * that page renders one domain card per catalog and reads its mode through this
 * map, so adding a new `CatalogId` (e.g. Threat Modeling in KIR-3925) here — with
 * its `PolicyKey` — makes the card appear automatically, no coverage-page edit.
 */
export const CATALOG_POLICY_KEY = {
    [CatalogId.SecureCoding]: PolicyKey.AiInstructionCatalog,
    [CatalogId.AgentInstructions]: PolicyKey.AiAgentInstructions,
    [CatalogId.ThreatModeling]: PolicyKey.ThreatModeling,
};
export const CATALOG_META = {
    [CatalogId.SecureCoding]: {
        id: CatalogId.SecureCoding,
        title: 'Secure AI Coding Enforcement',
        description: 'Secure coding guidance for AI coding agents. Choose which frameworks and categories are included in the audit, and manage custom policies.',
    },
    [CatalogId.AgentInstructions]: {
        id: CatalogId.AgentInstructions,
        title: 'AI Agent Instructions',
        description: 'Your rules for how AI coding agents should behave. Kirin audits instruction files against these categories and reports missing guidance.',
    },
    [CatalogId.ThreatModeling]: {
        id: CatalogId.ThreatModeling,
        title: 'Threat Modeling',
        description: 'Rules and best practices that make AI coding agents model threats before writing code, using STRIDE and approved mitigations. Choose which categories are included in the audit, and add your own.',
    },
};
/**
 * Framework tab label within a catalog. In the AI Agent Instructions catalog the
 * single `custom` framework is presented as "AI Agent Instructions"; everywhere
 * else the standard framework label is used.
 */
export function catalogFrameworkLabel(catalogId, framework) {
    if (catalogId === CatalogId.AgentInstructions &&
        framework === CatalogFramework.Custom) {
        return CATALOG_META[CatalogId.AgentInstructions].title;
    }
    return CATALOG_FRAMEWORK_META[framework].label;
}
/**
 * Framework tab description within a catalog. In the AI Agent Instructions
 * catalog the single `custom` framework describes agent-behavior rules, not the
 * generic "Custom / Proprietary" blurb; everywhere else the standard framework
 * description is used.
 */
export function catalogFrameworkDescription(catalogId, framework) {
    if (catalogId === CatalogId.AgentInstructions &&
        framework === CatalogFramework.Custom) {
        return 'Your rules for how AI coding agents should behave. Five ship out of the box; use Add category to create more.';
    }
    if (catalogId === CatalogId.ThreatModeling &&
        framework === CatalogFramework.Custom) {
        return 'Your own threat modeling rules. Add categories here or edit the ones you need.';
    }
    return CATALOG_FRAMEWORK_META[framework].description;
}
/**
 * A single catalog category. Built-in categories have `isBuiltIn: true` and a
 * stable slug id; custom categories carry a uuid and timestamps. `enabled`
 * reflects the per-scope on/off state (built-ins default on).
 */
export const catalogCategorySchema = z.object({
    id: z.string().min(1),
    framework: catalogFrameworkSchema,
    name: z.string().min(1),
    severity: severitySchema,
    /** Framework reference (e.g. `A03:2021`, `AML.T0051`, `PW.5`, `INTERNAL-01`). */
    reference: z.string().nullable(),
    description: z.string(),
    /** Detection keywords the scanner looks for in instruction files. */
    keywords: z.array(z.string()),
    /** Example instruction text to add to an AI instruction file. */
    instruction: z.string(),
    isBuiltIn: z.boolean(),
    /** Whether this category is included in the audit for this scope. */
    enabled: z.boolean(),
    createdAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime().nullable(),
});
/**
 * Per-framework state (enabled/disabled for this scope).
 */
export const catalogFrameworkStateSchema = z.object({
    framework: catalogFrameworkSchema,
    enabled: z.boolean(),
});
/**
 * Count of categories per framework, for the Policies-page badges.
 */
export const catalogCountsSchema = z.object({
    [CatalogFramework.Owasp]: z.number().int().nonnegative(),
    [CatalogFramework.Atlas]: z.number().int().nonnegative(),
    [CatalogFramework.Nist]: z.number().int().nonnegative(),
    // KIR-3925: defaulted so a new frontend bundle still parses a response from
    // a backend that predates the Threat Modeling catalog (the two deploy
    // independently).
    [CatalogFramework.ThreatModeling]: z.number().int().nonnegative().default(0),
    [CatalogFramework.Custom]: z.number().int().nonnegative(),
});
/**
 * Full catalog response: built-in + org custom categories (with overrides
 * applied), per-framework enabled state, plus counts.
 */
export const catalogResponseSchema = z.object({
    categories: z.array(catalogCategorySchema),
    frameworks: z.array(catalogFrameworkStateSchema),
    counts: catalogCountsSchema,
});
/** Shared field constraints for category create/edit forms. */
const categoryNameField = z.string().min(1).max(200);
const categoryDescriptionField = z.string().min(1).max(2000);
const categoryKeywordsField = z
    .array(z.string().min(1).max(120))
    .min(1)
    .max(50);
const categoryInstructionField = z.string().min(1).max(4000);
/**
 * Create-custom-category request body. `framework` is always `custom`
 * server-side; `reference` is optional and, when present, must be non-empty
 * (omit it entirely for "no reference").
 */
export const newCustomCategorySchema = z.object({
    name: categoryNameField,
    severity: severitySchema,
    reference: z.string().min(1).max(120).optional(),
    description: categoryDescriptionField,
    keywords: categoryKeywordsField,
    instruction: categoryInstructionField,
});
/**
 * Edit-custom-category request body. All fields editable, same constraints as
 * create. `reference` optional (omit for "no reference").
 */
export const editCustomCategorySchema = newCustomCategorySchema;
/**
 * Edit-built-in-category request body. Only severity, keywords, and example
 * instruction are editable; name and framework reference are locked and cannot
 * be changed.
 */
export const editBuiltinCategorySchema = z.object({
    severity: severitySchema,
    keywords: categoryKeywordsField,
    instruction: categoryInstructionField,
});
/**
 * Enable/disable request body (used for both frameworks and categories).
 */
export const catalogToggleSchema = z.object({
    enabled: z.boolean(),
});
/**
 * A persisted custom category (create / edit response, list item).
 */
export const customCategorySchema = catalogCategorySchema.extend({
    framework: z.literal(CatalogFramework.Custom),
    isBuiltIn: z.literal(false),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=ai-instruction-catalog.js.map